export interface Item {
  itemEntityId: string;
  itemId: string | null;
  requestId: string | null;
  requestEntityId: string | null;
  productEntityId: string | null;
  productId: string | null;
  description: string | null;
  country: string | null;
  classificationType: number | null;
  priority: number | null;
  dueDate: string | Date | null;
  status: number | null;
  airEntityId: string | null;
  airId: string | null;
  classificationCode: string | null;
  classificationDate: string | Date | null;
  requester: string | null;
  requesterEntityId: string | null;
  comment: string | null;
  subdivision: string | null;
  endUse: string | null;
  technicalEngineerEntityIds: string[] | null;
  technicalEngineers: string[] | null;
  updatedOn: string | Date | null;
}

export enum ItemPgaStage {
  Info = 1,
  Analysis = 2,
  Completed = 3,
}

export enum ItemAdCvdStage {
  Info = 1,
  Analysis = 2,
  Completed = 3,
}

export interface ItemDetails {
  itemId: string | null;
  itemEntityId: string | null;
  requestId: string | null;
  country: string | null;
  status: number | null;
  dueDate: string | Date | null;
  classificationType: number | null;
  subdivision: string | null;
  priority: number | null;
  requestedDate: string | Date | null;
  comment: string | null;
  cancellationReason: number | null;

  classificationCode: string | null;
  rationale: string | null;
  gri: GRI[] | null;
  rulings: string | null;
  source: string | null;
  productTranslation: string | null;
  adValoremDuty: string | null;
  pgaFlags: string | null;
  adcvd: boolean | null;
  portalUnicoAttributes: boolean | null;
  endUse: string | null;
  hasSpecialCustomsCodes: boolean | null;
  pgaStage: ItemPgaStage | null;
  adCvdStage: ItemAdCvdStage | null;
  brokerImporterClientCode: string;
  customsSeller: string;
  relatedPartyTransaction: boolean | null;
  cbam: string | null;
}

export interface ProductItemDetails {
  productId: string | null;
  description: string | null;
  materialGroup: string | null;
  technicalNorm: string | null;
  technicalClassificationStandard: string | null;
  procurementGroup: string | null;
  accountingGroup: string | null;
  salesGroup: string | null;
  technicalAttribute1: string | null;
  technicalAttribute2: string | null;
  technicalAttribute3: string | null;
}
export enum ItemStatus {
  Submitted = 0,
  InProgress = 1,
  PendingInformation = 2,
  Completed = 3,
  Cancelled = 4,
}

export enum ItemPriority {
  Low = 0,
  Medium = 1,
  Standard = 2,
  High = 3,
  Critical = 4,
}

export enum ItemCancellationReason {
  ClassifiedByRequestor = 0,
  DiscontinuedProduct = 1,
  EY_Error = 2,
  IntangibleGood = 3,
  RequestorError = 4,
  AllItemsCancelled = 5,
  NoResponseToInformationRequest = 6,
}

export enum ItemClassificationType {
  Import = 0,
  Export = 1,
  ECCN = 2,
}

export enum ItemClassificationCodeStatus {
  Active = 0,
  Inactive = 1,
  VerificationRequired = 2,
  Invalid = 3,
  InactiveReview = 4,
  InactiveReclassified = 5,
  InactiveIgnore = 6,
}

export enum GRI {
  GRI1 = 0,
  GRI2A = 1,
  GRI2B = 2,
  GRI3A = 3,
  GRI3B = 4,
  GRI3C = 5,
  GRI4 = 6,
  GRI5A = 7,
  GRI5B = 8,
  GRI6 = 9,
}

export function getItemStatusName(status: ItemStatus | null): string {
  switch (status) {
    case ItemStatus.Submitted:
      return 'item-status.submitted';
    case ItemStatus.InProgress:
      return 'item-status.in-progress';
    case ItemStatus.PendingInformation:
      return 'item-status.pending-information';
    case ItemStatus.Completed:
      return 'item-status.completed';
    case ItemStatus.Cancelled:
      return 'item-status.cancelled';
    default:
      return 'item-status.unknown';
  }
}

export function getItemClassificationTypeStatusName(status: ItemClassificationCodeStatus | null): string {
  switch (status) {
    case ItemClassificationCodeStatus.Active:
      return 'item-classification-code-status.active';
    case ItemClassificationCodeStatus.Inactive:
      return 'item-classification-code-status.inactive';
    case ItemClassificationCodeStatus.VerificationRequired:
      return 'item-classification-code-status.verification-required';
    case ItemClassificationCodeStatus.Invalid:
      return 'item-classification-code-status.invalid';
    case ItemClassificationCodeStatus.InactiveReview:
      return 'item-classification-code-status.inactive-review';
    case ItemClassificationCodeStatus.InactiveReclassified:
      return 'item-classification-code-status.inactive-reclassified';
    case ItemClassificationCodeStatus.InactiveIgnore:
      return 'item-classification-code-status.inactive-ignore';
    default:
      return 'item-classification-code-status.unknown';
  }
}

export function getItemClassificationTypeStatusClassName(status: ItemClassificationCodeStatus | null): string {
  switch (status) {
    case ItemClassificationCodeStatus.Active:
      return 'completed';
    case ItemClassificationCodeStatus.VerificationRequired:
      return 'pending-information';
    default:
      return 'unknown';
  }
}

export function getItemStatusChipClassName(status: ItemStatus | null): string {
  switch (status) {
    case ItemStatus.Submitted:
      return 'submitted';
    case ItemStatus.InProgress:
      return 'in-progress';
    case ItemStatus.PendingInformation:
      return 'pending-information';
    case ItemStatus.Completed:
      return 'completed';
    case ItemStatus.Cancelled:
      return 'cancelled';
    default:
      return 'unknown';
  }
}

export function getItemPriorityName(priority: ItemPriority | null): string {
  switch (priority) {
    case ItemPriority.Low:
      return 'item-priority.low';
    case ItemPriority.Medium:
      return 'item-priority.medium';
    case ItemPriority.Standard:
      return 'item-priority.standard';
    case ItemPriority.High:
      return 'item-priority.high';
    case ItemPriority.Critical:
      return 'item-priority.critical';
    default:
      return 'item-priority.unknown';
  }
}

export function getItemClassificationTypeName(classificationType: ItemClassificationType | null): string {
  switch (classificationType) {
    case ItemClassificationType.Import:
      return 'item-classification-type.import';
    case ItemClassificationType.Export:
      return 'item-classification-type.export';
    case ItemClassificationType.ECCN:
      return 'item-classification-type.eccn';
    default:
      return 'item-classification-type.unknown';
  }
}

export function getItemCancellationReasonName(cancellationReason: ItemCancellationReason | null): string {
  switch (cancellationReason) {
    case ItemCancellationReason.ClassifiedByRequestor:
      return 'item-cancellation-reason.classified-by-requestor';
    case ItemCancellationReason.DiscontinuedProduct:
      return 'item-cancellation-reason.discontinued-product';
    case ItemCancellationReason.EY_Error:
      return 'item-cancellation-reason.ey-error';
    case ItemCancellationReason.IntangibleGood:
      return 'item-cancellation-reason.intangible-good';
    case ItemCancellationReason.RequestorError:
      return 'item-cancellation-reason.requestor-error';
    case ItemCancellationReason.AllItemsCancelled:
      return 'item-cancellation-reason.all-items-cancelled';
    case ItemCancellationReason.NoResponseToInformationRequest:
      return 'item-cancellation-reason.no-response-to-information-request';
    default:
      return 'item-cancellation-reason.unknown';
  }
}

export function getGRIName(gri: GRI | null): string {
  switch (gri) {
    case GRI.GRI1:
      return 'gri.1';
    case GRI.GRI2A:
      return 'gri.2a';
    case GRI.GRI2B:
      return 'gri.2b';
    case GRI.GRI3A:
      return 'gri.3a';
    case GRI.GRI3B:
      return 'gri.3b';
    case GRI.GRI3C:
      return 'gri.3c';
    case GRI.GRI4:
      return 'gri.4';
    case GRI.GRI5A:
      return 'gri.5a';
    case GRI.GRI5B:
      return 'gri.5b';
    case GRI.GRI6:
      return 'gri.6';
    default:
      return 'gri.unknown';
  }
}

export interface ItemLabel {
  key: string;
  value: number;
  class: string;
}

export interface StatsItem {
  status: number;
  count: number;
  displayName?: string;
}

export interface PortalUnicoAttribute {
  code: string | null;
  parentCode: string | null;
  name: string | null;
  mandatory: boolean | null;
  value: string | null;
  objectives: string | null;
  agencies: string | null;
}

export enum PortalUnicoType {
  Default = 0,
  ThomsonReuters = 1,
}
